import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClientProvider } from 'react-query';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router-dom';
// Required so we can use the css prop in JSX across our app
// The empty import will be compiled out
// https://styled-components.com/docs/api#usage-with-typescript
import type {} from 'styled-components/cssprop';

import { FontFamily } from 'jf/components/FontFamily';
import { AuthAccessPage } from 'jf/pages/auth/AuthAccessPage';
import { AuthPage } from 'jf/pages/auth/AuthPage';
import { CreateAccountForm } from 'jf/pages/auth/CreateAccountForm';
import { OnboardingPage } from 'jf/pages/auth/OnboardingPage';
import { ResetPasswordForm } from 'jf/pages/auth/ResetPasswordForm';
import { SetPasswordForm } from 'jf/pages/auth/SetPasswordForm';
import { SignInForm } from 'jf/pages/auth/SignInForm';
import { TermsPage } from 'jf/pages/auth/TermsPage';
import { ErrorPage } from 'jf/pages/error/ErrorPage';
import { TopicsTab } from 'jf/pages/studyAnalyzer/topicDrawer/TopicsTab';
import { StudyBuilderController } from 'jf/pages/studyBuilder/StudyBuilderController';
import { StudySequencePage } from 'jf/pages/studySequence/StudySequencePage';
import { TeamsPage } from 'jf/pages/teams/TeamsPage';
import { queryClient } from 'jf/utils/queryClient';
// import types for what we set globally
import type {} from 'jf/utils/WindowTypes';

import { OpenAPI } from '../api';
import { StudiesController } from '../pages/studies/StudiesController';
import { StudyController } from '../pages/study/StudyController';
import { InsightsTab } from '../pages/studyAnalyzer/insights/InsightsTab';
import { ParticipationTab } from '../pages/studyAnalyzer/participation/ParticipationTab';
import { ResultsTab } from '../pages/studyAnalyzer/results/ResultsTab';
import { StudyAnalyzerPage } from '../pages/studyAnalyzer/StudyAnalyzerPage';
import { StudyRevealController } from '../pages/studyAnalyzer/studyReveal/studyReveal';
import { getCookie } from '../utils/getCookie';

import { DevExThemeProvider } from './themes/DevExTheme';
import { DevExErrorBoundary } from './DevExErrorBoundary';
import { DevExWrapper } from './DevExWrapper';

import 'jf/utils/logrocket-init';

export const refreshOpenAPIHeaders = () => {
  const headers: NonNullable<typeof OpenAPI.HEADERS> = {};

  // send latest CSRFToken
  headers['X-CSRFToken'] = getCookie('devex_csrftoken')!;

  // send scope_token for authorization if needed
  const queryParams = new URLSearchParams(window.location.search);
  const scopeToken = queryParams.get('scope_token');
  if (scopeToken) {
    headers.Authorization = `ScopeToken ${scopeToken}`;
  }

  OpenAPI.HEADERS = headers;
};

refreshOpenAPIHeaders();

const ROOBERT = <FontFamily name="Roobert" weights={['Light', 'Regular', 'Medium', 'SemiBold']} />;

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="" ErrorBoundary={DevExErrorBoundary}>
      {/* REQUIRE AUTH + NAV */}
      <Route path="/" element={<DevExWrapper reqAuth showNav />}>
        <Route index element={<StudiesController />} />
        <Route path={'studies/'} element={<StudiesController />} />
        <Route path={'teams/'} element={<TeamsPage />} />
        <Route path={'series/:sequenceRef/'} element={<StudySequencePage />} />
        <Route path={'series/:sequenceRef/team/:teamRef/'} element={<StudySequencePage />} />
        <Route path={'study/:studyRef/analyze/'} element={<StudyAnalyzerPage />}>
          <Route index path="insights/" element={<InsightsTab />} />
          <Route path="topics/" element={<TopicsTab />} />
          <Route path="questions/" element={<ResultsTab />} />
          <Route path="participation/" element={<ParticipationTab />} />
          <Route path=":teamRef/topics" element={<TopicsTab />} />
          <Route path=":teamRef/insights" element={<InsightsTab />} />
          <Route path=":teamRef/questions" element={<ResultsTab />} />
          <Route path=":teamRef/participation" element={<ParticipationTab />} />
        </Route>
      </Route>

      {/* REQUIRE AUTH + NO NAV */}
      <Route path="/" element={<DevExWrapper reqAuth />}>
        <Route path="study/:studyRef/analyze/reveal" element={<StudyRevealController />} />
        <Route path={'auth/'} element={<AuthPage />}>
          <Route path={'set-password'} element={<SetPasswordForm />} />
        </Route>
        <Route path={'auth/onboarding'} element={<OnboardingPage />} />
        <Route path="study/build" element={<StudyBuilderController />} />
        <Route path="study/:studyRef/build" element={<StudyBuilderController />} />
        <Route path="study/:studyRef/build/:step" element={<StudyBuilderController />} />
      </Route>

      {/* OPTIONAL AUTH + NO NAV */}
      <Route path="/" element={<DevExWrapper />}>
        <Route path={'study/:studyRef'} element={<StudyController />} />
        <Route path={'study/:studyRef/:targetRef'} element={<StudyController />} />
        <Route path="error" element={<ErrorPage />} />
        <Route path="terms" element={<TermsPage />} />
        <Route
          path="*"
          element={
            <ErrorPage
              code={404}
              message="Sorry, we couldn't find the page you were looking for."
            />
          }
        />
      </Route>

      {/* REQUIRE NO AUTH */}
      <Route path="/" element={<DevExWrapper reqNoAuth />}>
        <Route path={'auth/'} element={<AuthPage />}>
          <Route path={'sign-in'} element={<SignInForm />} />
          <Route path={'create-account'} element={<CreateAccountForm />} />
          <Route path={'reset-password'} element={<ResetPasswordForm />} />
        </Route>
        <Route path={'auth/access'} element={<AuthAccessPage />} />
      </Route>
    </Route>
  )
);

export const DevEx: React.FC = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <DevExThemeProvider>
          {ROOBERT}
          <RouterProvider router={router} />
        </DevExThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};
